import React from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { store, persistor } from "./store/index";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import Bugsnag from "@bugsnag/js";
import BugsnagPluginReact from "@bugsnag/plugin-react";
import "./components/styles/variables.css";
import "./index.css";
import ErrorBoundaryView from "components/ErrorBoundaryView";
import "intersection-observer";
import { isAuthenticated } from "./services/Auth";
import { apiMain } from "./services";

Error.stackTraceLimit = 1000;

Bugsnag.start({
  apiKey: process.env.REACT_APP_BUGSNAG_API_KEY ?? "",
  appVersion: process.env.REACT_APP_VERSION ?? "dev",
  releaseStage: process.env.REACT_APP_ENV ?? "development",
  enabledReleaseStages: ["production", "staging"],
  plugins: [new BugsnagPluginReact()],
  onError: async (event) => {
    const errors = event.errors;
    const error = errors[0];
    const { errorClass, errorMessage, stacktrace } = error;

    if (errorClass === "AbortError" || errorClass === "NotSupportedError") {
      return false;
    }

    // WTF, happens somewhere inside node_modules/react-dom/cjs/react-dom.production.min.js
    // Looks like some destruction code that happens on tab close.
    if (errorMessage === "Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node.") {
      return false;
    }

    // Looks like not something that we control.
    if (errorClass === "TypeError" && errorMessage.indexOf("Failed to load https://cdn.jsdelivr.net/npm/") === 0) {
      return false;
    }

    // eslint-disable-next-line
    if (errorClass === "Error" && errorMessage === "Minified React error #185; visit https://reactjs.org/docs/error-decoder.html?invariant=185 for the full message or use the non-minified dev environment for full errors and additional helpful warnings.") {
      return false;
    }

    if (errorClass === "ChunkLoadError") {
      return false;
    }

    if (errorClass === "Error" && errorMessage.includes("Loading CSS chunk")) {
      return false;
    }

    const isExternalError = stacktrace.find(({ file }) => {
      return !file.startsWith(`${window.location.origin}/static/js/`);
    });

    if (isExternalError) {
      return false;
    }

    event.addMetadata("errors", errors);

    if ((await isAuthenticated())) {
      const user = await apiMain.getMe();
      event.setUser(user.username);
    }

    return true;
  },
});

// @ts-ignore
const ErrorBoundary = Bugsnag.getPlugin("react").createErrorBoundary(React);

const root = createRoot(
  document.getElementById("root") as HTMLElement
);

root.render(
  <ErrorBoundary FallbackComponent={ErrorBoundaryView}>
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <App />
      </PersistGate>
    </Provider>
  </ErrorBoundary>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
